import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Base from '../base';
import { Alert, Row, Col, Card, Badge, Button, Spinner } from '@themesberg/react-bootstrap';
import { get, post } from '../../services/back.service';
import ModalFormPqrResponse from '../../components/forms/pqr/modal.form.pqr.response';
import ModalMessage from '../../components/widgets/modalMessage';

const STATE_BADGE = {
  pending: 'warning',
  in_progress: 'info',
  resolved: 'success',
  closed: 'secondary',
  cancelled: 'danger',
};

const PRIORITY_BADGE = {
  low: 'success',
  medium: 'warning',
  high: 'danger',
  urgent: 'danger',
};

const STATE_LABEL = {
  pending: 'Pendiente',
  in_progress: 'En Proceso',
  resolved: 'Resuelto',
  closed: 'Cerrado',
  cancelled: 'Cancelado',
};

const PRIORITY_LABEL = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  urgent: 'Urgente',
};

function PqrDetail() {
  const { id } = useParams();
  const [pqr, setPqr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [showResolve, setShowResolve] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [resolution, setResolution] = useState('');

  const getDetail = async () => {
    setLoading(true);
    const response = await get('/pqr/' + id);
    if (response.success) {
      setPqr(response.data);
    } else {
      setAlert({ show: true, message: response.message, type: 'danger' });
    }
    setLoading(false);
  };

  useEffect(() => {
    getDetail();
  }, [id]);

  const afterResponse = (variant, msg) => {
    setAlert({ show: true, message: msg, type: variant });
    getDetail();
  };

  const handleResolve = async () => {
    const body = { resolution };
    const resp = await post('/pqr/' + id + '/resolve', body);
    if (resp.success) {
      setAlert({ show: true, message: 'PQR resuelta exitosamente', type: 'success' });
    } else {
      setAlert({ show: true, message: resp.message, type: 'danger' });
    }
    setShowResolve(false);
    setResolution('');
    getDetail();
  };

  const handleClose = async () => {
    const resp = await post('/pqr/' + id + '/close', {});
    if (resp.success) {
      setAlert({ show: true, message: 'PQR cerrada exitosamente', type: 'success' });
    } else {
      setAlert({ show: true, message: resp.message, type: 'danger' });
    }
    setShowClose(false);
    getDetail();
  };

  const handleCancel = async () => {
    const resp = await post('/pqr/' + id + '/cancel', {});
    if (resp.success) {
      setAlert({ show: true, message: 'PQR cancelada exitosamente', type: 'success' });
    } else {
      setAlert({ show: true, message: resp.message, type: 'danger' });
    }
    setShowCancel(false);
    getDetail();
  };

  if (loading) {
    return (
      <Base>
        <div className="text-center mt-5">
          <Spinner animation="border" role="status" />
        </div>
      </Base>
    );
  }

  if (!pqr) {
    return (
      <Base>
        <Alert variant="danger">PQR no encontrada</Alert>
      </Base>
    );
  }

  return (
    <Base>
      {alert.show && (
        <Alert variant={alert.type} onClose={() => setAlert({ show: false })} dismissible>
          {alert.message}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={8}>
              <h4>PQR #{pqr.radicate}</h4>
              <p className="mb-1"><strong>Tipo:</strong> {pqr.type_name}</p>
              <p className="mb-1"><strong>Cliente:</strong> {pqr.client_name}</p>
              <p className="mb-1"><strong>Email:</strong> {pqr.client_email}</p>
              {pqr.client_phone && <p className="mb-1"><strong>Teléfono:</strong> {pqr.client_phone}</p>}
              {pqr.client_document && <p className="mb-1"><strong>Documento:</strong> {pqr.client_document}</p>}
              {pqr.inspection_placa && (
                <p className="mb-1"><strong>Inspección (Placa):</strong> {pqr.inspection_placa}</p>
              )}
              <p className="mb-1"><strong>Creado por:</strong> {pqr.created_by_name || 'Página web'}</p>
              <p className="mb-1"><strong>Fecha:</strong> {pqr.created}</p>
            </Col>
            <Col md={4} className="text-md-end">
              <div className="mb-2">
                <Badge bg={STATE_BADGE[pqr.state]} className="me-2">
                  {STATE_LABEL[pqr.state] || pqr.state}
                </Badge>
                <Badge bg={PRIORITY_BADGE[pqr.priority]}>
                  {PRIORITY_LABEL[pqr.priority] || pqr.priority}
                </Badge>
              </div>
              {pqr.assigned_to_name && (
                <p className="mb-1"><strong>Asignado a:</strong> {pqr.assigned_to_name}</p>
              )}
              {pqr.office_name && (
                <p className="mb-1"><strong>Oficina:</strong> {pqr.office_name}</p>
              )}
            </Col>
          </Row>
          <hr />
          <h6>Descripción</h6>
          <p>{pqr.description}</p>

          {pqr.resolution && (
            <>
              <hr />
              <h6>Resolución</h6>
              <p>{pqr.resolution}</p>
              {pqr.resolution_date && <small className="text-muted">Resuelto el: {pqr.resolution_date}</small>}
            </>
          )}

          <hr />
          <Row className="g-2">
            <Col xs="auto">
              <ModalFormPqrResponse
                pqrId={id}
                title="Agregar Respuesta"
                button_text="Agregar Respuesta"
                callback={afterResponse}
              />
            </Col>
            {pqr.state === 'pending' || pqr.state === 'in_progress' ? (
              <>
                <Col xs="auto">
                  <Button variant="outline-success" onClick={() => setShowResolve(true)}>
                    Resolver
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button variant="outline-secondary" onClick={() => setShowClose(true)}>
                    Cerrar
                  </Button>
                </Col>
              </>
            ) : null}
            {pqr.state === 'pending' ? (
              <Col xs="auto">
                <Button variant="outline-danger" onClick={() => setShowCancel(true)}>
                  Cancelar
                </Button>
              </Col>
            ) : null}
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Historial de Respuestas</h5>
        </Card.Header>
        <Card.Body>
          {pqr.responses && pqr.responses.length > 0 ? (
            pqr.responses.map((r, i) => (
              <div key={r.id} className={`mb-3 pb-3 ${i < pqr.responses.length - 1 ? 'border-bottom' : ''}`}>
                <div className="d-flex justify-content-between">
                  <strong>{r.user_name || 'Anónimo'}</strong>
                  <small className="text-muted">{r.created}</small>
                </div>
                <p className="mt-1 mb-0">{r.message}</p>
              </div>
            ))
          ) : (
            <p className="text-muted mb-0">Sin respuestas aún</p>
          )}
        </Card.Body>
      </Card>

      <ModalMessage
        title="Resolver PQR"
        text={
          <div>
            <p>Describa la resolución de esta PQR:</p>
            <textarea
              className="form-control"
              rows={3}
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              placeholder="Describa la resolución..."
            />
          </div>
        }
        showProps={showResolve}
        funcAccept={handleResolve}
        funcClose={() => { setShowResolve(false); setResolution(''); }}
      />

      <ModalMessage
        title="Cerrar PQR"
        text="¿Está seguro que desea cerrar esta PQR?"
        showProps={showClose}
        funcAccept={handleClose}
        funcClose={() => setShowClose(false)}
      />

      <ModalMessage
        title="Cancelar PQR"
        text="¿Está seguro que desea cancelar esta PQR?"
        showProps={showCancel}
        funcAccept={handleCancel}
        funcClose={() => setShowCancel(false)}
      />
    </Base>
  );
}

export default PqrDetail;
